import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Gift, Sparkles, Trophy, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { BACKEND_URL } from '../config';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    green: '#22c55e',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const InstantRedeemSuccess = ({ result, onExploreDeals, onViewRewards }) => {
  const { 
    business_name, 
    business_logo_url, 
    deal_title, 
    deal_description,
    redemption_code,
    total_taps_earned,
    first_redemption_bonus,
    action 
  } = result;

  const isFirstRedemption = first_redemption_bonus > 0;
  const wasClaimedAndRedeemed = action === 'claimed_and_redeemed';

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: v5.colors.cream }}>
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <img src="/tappd-logo.png" alt="Tappd" className="mx-auto mb-8 h-12" />
          
          <div 
            className="rounded-xl p-8"
            style={{ 
              backgroundColor: v5.colors.white,
              border: `1px solid ${v5.colors.borderLight}`,
              borderLeft: `4px solid ${v5.colors.green}`
            }}
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
            >
              <CheckCircle2 className="w-14 h-14" style={{ color: v5.colors.green }} />
            </motion.div>
            
            <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
              // deal_redeemed
            </p>
            
            <h1 style={{ fontFamily: v5.fonts.display, fontSize: '32px', fontWeight: 500, color: v5.colors.green, marginBottom: '16px' }}>
              {wasClaimedAndRedeemed ? 'Deal Claimed & Redeemed!' : 'Deal Redeemed!'}
            </h1>
            
            {/* Taps Earned */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
            >
              <Sparkles className="w-5 h-5" style={{ color: v5.colors.purple }} />
              <span style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.purple }}>
                +{total_taps_earned} Taps earned
              </span>
              {isFirstRedemption && (
                <span 
                  className="px-2 py-0.5 rounded-full text-xs"
                  style={{ backgroundColor: v5.colors.purple, color: v5.colors.white }}
                >
                  +{first_redemption_bonus} First Redeem Bonus!
                </span>
              )}
            </motion.div>
            
            {/* Business & Deal Info */}
            <div 
              className="rounded-lg p-4 mb-6 text-left"
              style={{ backgroundColor: v5.colors.cream }}
            >
              <div className="flex items-start gap-4">
                {business_logo_url ? (
                  <img 
                    src={resolveAssetUrl(business_logo_url)}
                    alt={business_name}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div 
                    className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                  >
                    <Gift className="w-7 h-7" style={{ color: v5.colors.purple }} />
                  </div>
                )}
                <div>
                  <p style={{ fontFamily: v5.fonts.body, fontSize: '16px', fontWeight: 600, color: v5.colors.textPrimary }}>
                    {business_name}
                  </p>
                  <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textSecondary, marginTop: '4px' }}>
                    {deal_title}
                  </p>
                  {deal_description && (
                    <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted, marginTop: '4px' }}>
                      {deal_description}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Redemption Code */}
            <div 
              className="rounded-lg p-4 mb-6"
              style={{ backgroundColor: v5.colors.navy }}
            >
              <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                // show_to_staff
              </p>
              <p style={{ fontFamily: v5.fonts.mono, fontSize: '32px', fontWeight: 700, color: v5.colors.white, letterSpacing: '0.2em' }}>
                {redemption_code}
              </p>
            </div>
            
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginBottom: '24px' }}>
              Show this screen to the staff to redeem your deal
            </p>
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={onViewRewards}
                className="w-full rounded-xl py-5 text-base font-semibold"
                style={{
                  background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                  color: v5.colors.white,
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                }}
              >
                <Trophy className="w-5 h-5 mr-2" />
                View My Rewards
              </Button>
              
              <Button
                onClick={onExploreDeals}
                variant="outline"
                className="w-full rounded-xl py-5 text-base font-semibold"
                style={{
                  border: `2px solid ${v5.colors.borderDefault}`,
                  color: v5.colors.textPrimary,
                  backgroundColor: 'transparent'
                }}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Explore More Deals
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InstantRedeemSuccess;
